import type { TFunction } from 'i18next';

export const BAND_KEY_ORDER = [
  'above_plan',
  'on_plan',
  'medium_risk',
  'at_risk',
] as const;

export type BandKey = (typeof BAND_KEY_ORDER)[number];

export interface RagBand {
  key: BandKey;
  enabled: number;
  threshold: number | null;
}

export interface BandDefinitionRow {
  key: BandKey;
  color: string;
  label: string;
  description: string;
}

const PROGRESS_COLOR_TO_BAND: Record<number, BandKey> = {
  1: 'above_plan',
  2: 'on_plan',
  3: 'medium_risk',
  4: 'at_risk',
};

export function progressColorToBandKey(
  progressColorRaw: string | number | null | undefined,
): BandKey {
  const index = Number(progressColorRaw);
  return PROGRESS_COLOR_TO_BAND[index] ?? 'at_risk';
}

/**
 * The KR's direction sentence. `counting` mirrors Metric::METRIC_COUNTING_* on the server;
 * surveys (5-7) count up, which is also MetricFacade::getMetricResultDirection's fallback.
 */
export function directionText(counting: string | number, t: TFunction): string {
  switch (String(counting)) {
    case '2':
      return t('should decrease to');
    case '3':
      return t('should be equal to');
    case '4':
      return t('should stay in the range');
    case '8':
      return t('should not exceed');
    case '9':
      return t('should not fall below');
    default:
      return t('should increase to');
  }
}

/** Range KRs carry both limits in one display string ("lower - upper"); read it as a sentence. */
export function directionTargetText(
  counting: string | number,
  targetDisplay: string,
  t: TFunction,
): string {
  const [lower, upper] = targetDisplay.split(' - ');
  if (String(counting) !== '4' || !upper) {
    return targetDisplay;
  }
  return t('{{lower}} to {{upper}}', { lower, upper });
}

/** Not-exceed (8) and not-fall-below (9) KRs hold a limit rather than move toward a target. */
export function isGuardrailCounting(counting: string | number): boolean {
  return ['8', '9'].includes(String(counting));
}

function buildBandDescription(
  band: RagBand,
  previousThreshold: number | null,
  t: TFunction,
): string {
  if (band.key === 'above_plan' && Number(band.threshold) > 0) {
    return t('on or ahead of expected progress, or up to {{threshold}}% behind', {
      threshold: band.threshold,
    });
  }
  if (band.key === 'above_plan') {
    return t('on or ahead of expected progress');
  }

  const { threshold } = band;

  if (threshold === null || threshold === undefined) {
    if (previousThreshold !== null && previousThreshold > 0) {
      return t('more than {{threshold}}% behind', { threshold: previousThreshold });
    }
    return t('any amount behind expected progress');
  }

  if (previousThreshold === null || previousThreshold === 0) {
    return t('up to {{threshold}}% behind', { threshold });
  }

  return t('{{from}}–{{to}}% behind', { from: previousThreshold, to: threshold });
}

const COMMITTED_BAND_COUNT = 2;

/** The two-band note fits only when a committed profile leaves the middle bands off. */
export function definitionsNote(
  isCommitted: boolean,
  rowCount: number,
  bandLabels: Record<BandKey, string>,
  t: TFunction,
): string {
  if (isCommitted && rowCount === COMMITTED_BAND_COUNT) {
    return t(
      'Committed Key Results use two bands only — no {{onPlan}} or {{mediumRisk}}. Thresholds are configurable per organization.',
      { onPlan: bandLabels.on_plan, mediumRisk: bandLabels.medium_risk },
    );
  }
  return t('Thresholds are configurable per organization.');
}

export function buildBandDefinitionRows(
  bands: RagBand[],
  bandLabels: Record<BandKey, string>,
  bandColors: Record<BandKey, string>,
  t: TFunction,
): BandDefinitionRow[] {
  const enabled = bands.filter((band) => band.enabled);
  const rows: BandDefinitionRow[] = [];
  let previousThreshold: number | null = null;

  for (const band of enabled) {
    const label = bandLabels[band.key] ?? band.key;
    rows.push({
      key: band.key,
      color: bandColors[band.key] ?? '#666',
      label,
      description: buildBandDescription(band, previousThreshold, t),
    });

    if (band.threshold !== null && band.threshold !== undefined) {
      previousThreshold = Number(band.threshold);
    }
  }

  return rows;
}

/**
 * Org band colors are user-configured, so a light one leaves white header text unreadable.
 * Threshold of 186 on perceived brightness per OKR-11384; unparseable colors keep white.
 */
export function readableTextColor(background: string): string {
  let hex = (background || '').trim().replace('#', '');
  // Three-digit shorthand is accepted by the admin validator, so it reaches us stored.
  if (/^[0-9a-fA-F]{3}$/.test(hex)) {
    hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
  }
  if (hex.length !== 6) {
    return '#FFFFFF';
  }
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) {
    return '#FFFFFF';
  }
  return r * 0.299 + g * 0.587 + b * 0.114 > 186 ? '#000000' : '#FFFFFF';
}
